<!--
    Visor de pictogramas basado en Three.js.
    Consume el output de cad/rendering/DxfScene.js.

    Esta carpeta reemplaza a viewer-konva/ y viewer-pixi/.
    Ambos quedan como legacy y se eliminarán cuando se complete la migración.
  -->

<template>
  <div ref="canvasContainer" class="canvasContainer"></div>
</template>

<script>
import * as three from "three"
import opentype from "opentype.js"
import { DxfScene } from "../cad/rendering/DxfScene"
import { DxfViewer } from "./DxfViewer"

import { createLogger } from "../utils/logger"
const logger = createLogger('Viewer')

export default {
  name: 'Viewer',

  props: {
    // cadJson producido por cad/formats/loadFromFile. Null hasta que el usuario suba un archivo.
    cadJson: {
      type: Object,
      default: null
    },
    /** Array de URLs a archivos .ttf. Se convierten en fontFetchers para el TextRenderer. */
    fonts: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      dxfViewer: null,  // Instancia vanilla de DxfViewer. Se crea en mounted(), se destruye en beforeDestroy().
    }
  },

  mounted() {
    this.dxfViewer = new DxfViewer(this.$refs.canvasContainer, {
      autoResize:  true,
      clearColor:  new three.Color('#ffffff'),  // fondo blanco (estilo AutoCAD light)
      antialias:   true,
      // Habilita canal alpha en el WebGL context. No afecta la vista normal porque
      // clearColor sigue siendo blanco opaco, pero permite que Save({transparent:true})
      // produzca capturas con fondo transparente real.
      canvasAlpha: true,
    })

    this._setupEventForwarding()

    // Si ya hay cadJson cuando el componente monta, cargarlo
    if (this.cadJson) {
      this._loadCadJson(this.cadJson)
    }
  },

  beforeDestroy() {
    if (this.dxfViewer) {
      this.dxfViewer.Destroy()      // libera WebGL, materiales, observers, etc.
      this.dxfViewer = null
    }
  },

  watch: {
    cadJson(newVal) {
      if (newVal) {
        this._loadCadJson(newVal)
      } else {
        // Si cadJson vuelve a null (usuario clickó "limpiar"), reset el viewer
        this.dxfViewer?.Clear()
      }
    }
  },

  methods: {
    // ───────────────────────────────── API pública ─────────────────────────────────
    // La llama el Toolbar desde LayoutBuilder vía $refs.viewer.X()

    /**
     * Acceso público al DxfViewer vanilla — para que componentes externos
     * (ej. DxfDiffOverlay) puedan llamar GetScene(), GetOrigin(), Render(), etc.
     * y pintar capas extra sobre el render principal.
     */
    GetDxfViewer() {
      return this.dxfViewer
    },

    /** Ajusta cámara para que se vea toda la escena. */
    FitToScreen() {
      if (!this.dxfViewer?.HasRenderer()) return
      const bounds = this.dxfViewer.GetBounds()
      const origin = this.dxfViewer.GetOrigin()
      if (!bounds || !origin) return
      this.dxfViewer.FitView(
        bounds.minX - origin.x,
        bounds.maxX - origin.x,
        bounds.minY - origin.y,
        bounds.maxY - origin.y
      )
    },

    /** Zoom in con factor x1.2 sobre la cámara. */
    ZoomIn() {
      this._zoomBy(1.2)
    },

    /** Zoom out con factor x0.83 sobre la cámara. */
    ZoomOut() {
      this._zoomBy(1 / 1.2)
    },

    _zoomBy(factor) {
      const cam = this.dxfViewer?.GetCamera()
      if (!cam) return
      cam.zoom *= factor
      cam.updateProjectionMatrix()
      this.dxfViewer.Render()
    },

    /**
     * Pan está siempre activo (OrbitControls). Mantenemos el método para compatibilidad
     * con la API que invoca el Toolbar, pero es no-op por ahora.
     */
    EnableDraggable(enabled) {
      // OrbitControls está activo permanentemente con click-izquierdo = pan.
      // Si más adelante quieres deshabilitar el pan dinámicamente, accede a
      // this.dxfViewer.controls.enabled = enabled
    },

    /** Forza un render frame. */
    Render() {
      this.dxfViewer?.Render()
    },

    /**
     * Captura del canvas como PNG base64.
     * @param {Object}  opts
     * @param {number}  opts.scale       - multiplicador de resolución (1 = normal, 2 = nítido para zoom). Default 2.
     * @param {boolean} opts.transparent - true para fondo transparente. Default true.
     */
    Save({ scale = 2, transparent = true } = {}) {
      const renderer = this.dxfViewer?.GetRenderer?.()
      const canvas   = this.dxfViewer?.GetCanvas()
      if (!renderer || !canvas) return null

      // Snapshot del estado actual para restaurar después
      const origRatio      = renderer.getPixelRatio()
      const origClearColor = renderer.getClearColor(new three.Color())
      const origClearAlpha = renderer.getClearAlpha()
      const origWidth      = canvas.clientWidth
      const origHeight     = canvas.clientHeight

      try {
        // Sube la resolución sin cambiar el tamaño visible (3er param false → no toca CSS).
        renderer.setPixelRatio(scale)
        renderer.setSize(origWidth, origHeight, false)
        if (transparent) renderer.setClearColor(0x000000, 0)

        this.dxfViewer.Render()
        return canvas.toDataURL('image/png')
      } finally {
        // Restaurar siempre, incluso si toDataURL tira.
        renderer.setPixelRatio(origRatio)
        renderer.setSize(origWidth, origHeight, false)
        renderer.setClearColor(origClearColor, origClearAlpha)
        this.dxfViewer.Render()
      }
    },

    /** Lista de capas (para futuro panel de visibilidad). */
    GetLayers(nonEmptyOnly = false) {
      return this.dxfViewer?.GetLayers(nonEmptyOnly) ?? []
    },

    /** Toggle visibilidad de una capa. */
    ShowLayer(name, show) {
      this.dxfViewer?.ShowLayer(name, show)
    },

    /** Cambia opacidad de todas las entities de una capa. */
    SetLayerOpacity(name, opacity) {
      this.dxfViewer?.SetLayerOpacity(name, opacity)
    },

    /** Pinta una capa en gris uniforme (on=true) o restaura su color (on=false). */
    SetLayerGrayscale(name, on, gray) {
      this.dxfViewer?.SetLayerGrayscale(name, on, gray)
    },

    async _loadCadJson(cadJson) {
      try {
        // 1. Construir DxfScene a partir del cadJson.
        //    Se hace en main thread (es sincrónico/CPU-bound; el parser ya está hecho).
        const builder = new DxfScene()
        const fetchers = this._createFontFetchers(this.fonts)
        await builder.Build(cadJson, fetchers)
        // 2. Pasar la escena resultante al DxfViewer (vanilla).
        await this.dxfViewer.LoadScene(builder.scene, cadJson)
      } catch (err) {
        logger.error('load cadJson error:', err)
        this.$emit('epc-error', err.message)
      }
    },

    /**
     * Conecta los eventos custom del DxfViewer al sistema $emit de Vue.
     * El DxfViewer emite `__dxf_loaded`, `__dxf_pointerdown`, etc.
     * Aquí los re-emitimos como `epc-loaded`, `epc-pointerdown`, etc.
     */
    _setupEventForwarding() {
      const events = [
        'loaded',
        'cleared',
        'destroyed',
        'resized',
        'pointerdown',
        'pointerup',
        'viewChanged',
        'message',
      ]
      for (const ev of events) {
        this.dxfViewer.Subscribe(ev, (e) => {
          this.$emit('epc-' + ev, e.detail)
        })
      }
    },

    /**
     * Convierte URLs de archivos .ttf en "font fetchers" — funciones async que
     * descargan el TTF y lo parsean con opentype.js. Los invoca el TextRenderer
     * por demanda solo cuando encuentra glyphs sin resolver.
     */
    _createFontFetchers(urls) {
      if (!urls || urls.length === 0) return null
      return urls.map(url => async () => {
        const data = await fetch(url).then(r => r.arrayBuffer())
        return opentype.parse(data)
      })
    }
  }
}
</script>

<style scoped>
  .canvasContainer {
    width: 100%;
    height: 100%;
    position: relative;
    overflow: hidden;
  }
</style>
